<script>
  import { token } from "./store/token.js";
  import { onMount } from "svelte";
  import WorkspaceDetail from "./WorkspaceDetail.svelte";
  import { fetch_device_types } from "./api/request";
  import { FILTER_PLAN_OPTIONS } from "./lib/planOptions.js";
  import { route, navigate } from "./store/router.js";

  let workspaces = [];
  let loading = true;
  let error = null;
  let searchQuery = "";
  let debouncedSearchQuery = "";
  let searchDebounceTimeout;
  let debouncedMinSongCount = "";
  let debouncedMinMemberCount = "";
  let debouncedMinFolderCount = "";
  let debouncedMinPlaylistCount = "";
  let debouncedMinProjectCount = "";
  let filterDebounceTimeout;
  let sortField = "created_at";
  let sortDirection = "desc";
  // Selected workspace is driven by the URL (#/workspaces/:workspaceId) so the
  // detail view is deep-linkable and back/forward works.
  $: selectedWorkspaceId = $route.params.workspaceId ?? null;
  let minMemberCount = "";
  let minSongCount = "";
  let minFolderCount = "";
  let minPlaylistCount = "";
  let minProjectCount = "";
  let showMoreFilters = false;
  const PAGE_SIZE_OPTIONS = [20, 50, 100];
  let limit = 50;
  let offset = 0;
  let total = 0;
  let selectedPlanId = '';
  let selectedTrialStatus = '';
  let selectedDeviceType = '';
  let deviceTypes = [];
  let loadingDeviceTypes = false;

  const trialStatusOptions = [
    { value: '', label: 'All Trial Status' },
    { value: 'trial_active', label: 'Trial Active' },
    { value: 'trial_expired', label: 'Trial Expired' },
    { value: 'trial_abandoned', label: 'Trial Abandoned' },
  ];

  const loadDeviceTypes = async () => {
    if (!$token) return;
    loadingDeviceTypes = true;
    try {
      deviceTypes = await fetch_device_types($token);
    } catch (e) {
      console.error("Error fetching device types:", e);
      deviceTypes = [];
    } finally {
      loadingDeviceTypes = false;
    }
  };

  const buildListParams = () => {
    const params = [`limit=${limit}`, `offset=${offset}`];
    if (selectedPlanId === 'all-paid') params.push('all_paid=true');
    else if (selectedPlanId === 'all-free') params.push('all_free=true');
    else if (selectedPlanId) params.push(`plan_id=${encodeURIComponent(selectedPlanId)}`);
    if (selectedTrialStatus) params.push(`${selectedTrialStatus}=true`);
    if (selectedDeviceType) params.push(`device_type=${encodeURIComponent(selectedDeviceType)}`);
    if (debouncedSearchQuery?.trim()) params.push(`workspace_name=${encodeURIComponent(debouncedSearchQuery.trim())}`);
    if (debouncedMinSongCount !== '') params.push(`min_audio_count=${debouncedMinSongCount}`);
    if (debouncedMinMemberCount !== '') params.push(`min_member_count=${debouncedMinMemberCount}`);
    if (debouncedMinFolderCount !== '') params.push(`min_folder_count=${debouncedMinFolderCount}`);
    if (debouncedMinPlaylistCount !== '') params.push(`min_playlist_count=${debouncedMinPlaylistCount}`);
    if (debouncedMinProjectCount !== '') params.push(`min_project_count=${debouncedMinProjectCount}`);
    return params;
  };

  const fetchWorkspaces = async () => {
    try {
      loading = true;
      error = null;
      const fetchUrl = `${process.env.API_ROOT}/admin/workspace/list?${buildListParams().join('&')}`;
      const response = await fetch(fetchUrl, {
        headers: { Authorization: "Bearer " + $token },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      workspaces = data.workspaces ?? data.records ?? [];
      total = data.total ?? 0;
    } catch (e) {
      error = e.message;
      console.error("Error fetching workspaces:", e);
    } finally {
      loading = false;
    }
  };

  $: totalPages = total > 0 ? Math.ceil(total / limit) : 1;
  $: currentPage = Math.floor(offset / limit) + 1;
  $: hasNextPage = currentPage < totalPages;
  $: hasPrevPage = offset > 0;

  const goToPage = (pageNum) => {
    const nextOffset = Math.max(0, (pageNum - 1) * limit);
    if (nextOffset === offset) return;
    offset = nextOffset;
    fetchWorkspaces();
  };

  const onLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    if (newLimit === limit) return;
    limit = newLimit;
    offset = 0;
    fetchWorkspaces();
  };

  const sortWorkspaces = (field) => {
    if (sortField === field) {
      sortDirection = sortDirection === "asc" ? "desc" : "asc";
    } else {
      sortField = field;
      sortDirection = "desc";
    }
  };

  $: filteredWorkspaces = workspaces
    .sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      const modifier = sortDirection === "asc" ? 1 : -1;

      if (typeof aValue === "string") {
        return aValue.localeCompare(bValue) * modifier;
      }
      return (aValue - bValue) * modifier;
    });

  onMount(() => {
    loadDeviceTypes();
    fetchWorkspaces();
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleWorkspaceClick = (workspaceId) => {
    navigate("workspaces", { workspaceId });
  };

  const handleBack = () => {
    navigate("workspaces", {});
  };

  $: if (selectedPlanId !== undefined) {
    offset = 0;
    if ($token) fetchWorkspaces();
  }

  $: if (selectedTrialStatus !== undefined) {
    offset = 0;
    if ($token) fetchWorkspaces();
  }

  $: if (selectedDeviceType !== undefined) {
    offset = 0;
    if ($token) fetchWorkspaces();
  }

  $: if (searchQuery !== undefined) {
    clearTimeout(searchDebounceTimeout);
    searchDebounceTimeout = setTimeout(() => {
      debouncedSearchQuery = searchQuery;
    }, 400);
  }

  $: if (debouncedSearchQuery !== undefined) {
    offset = 0;
    if ($token) fetchWorkspaces();
  }

  $: if (minSongCount !== undefined || minMemberCount !== undefined || minFolderCount !== undefined || minPlaylistCount !== undefined || minProjectCount !== undefined) {
    clearTimeout(filterDebounceTimeout);
    filterDebounceTimeout = setTimeout(() => {
      debouncedMinSongCount = minSongCount;
      debouncedMinMemberCount = minMemberCount;
      debouncedMinFolderCount = minFolderCount;
      debouncedMinPlaylistCount = minPlaylistCount;
      debouncedMinProjectCount = minProjectCount;
    }, 400);
  }

  $: if (debouncedMinSongCount !== undefined || debouncedMinMemberCount !== undefined || debouncedMinFolderCount !== undefined || debouncedMinPlaylistCount !== undefined || debouncedMinProjectCount !== undefined) {
    offset = 0;
    if ($token) fetchWorkspaces();
  }

  const toggleMoreFilters = () => {
    showMoreFilters = !showMoreFilters;
  };

  const handleClickOutside = (event) => {
    if (showMoreFilters && !event.target.closest('.more-filters-container')) {
      showMoreFilters = false;
    }
  };

  $: if (typeof window !== 'undefined') {
    if (showMoreFilters) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }
  }

  $: hasActiveFilters = selectedPlanId || selectedTrialStatus || selectedDeviceType ||
    (debouncedSearchQuery?.trim()) || minSongCount !== "" || minMemberCount !== "" ||
    minFolderCount !== "" || minPlaylistCount !== "" || minProjectCount !== "";
</script>

{#if selectedWorkspaceId}
  <div class="detail-container">
    <button class="ds-btn ds-btn-secondary back-button" on:click={handleBack}>
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M19 12H5M12 19l-7-7 7-7"/>
      </svg>
      Back to Workspaces
    </button>
    <WorkspaceDetail workspaceId={selectedWorkspaceId} onWorkspaceDeleted={() => { navigate("workspaces", {}); fetchWorkspaces(); }} />
  </div>
{:else}
  <div class="container">
    <div class="ds-page-header">
      <div>
        <h1 class="ds-page-title">Workspaces</h1>
        {#if total > 0}
          <p class="ds-page-subtitle">
            {total} workspace{total !== 1 ? 's' : ''}
            {#if totalPages > 1}
              (page {currentPage} of {totalPages})
            {/if}
          </p>
        {/if}
      </div>
      <div class="search-wrap">
        <input
          type="text"
          bind:value={searchQuery}
          placeholder="Search workspaces..."
          class="ds-search"
          class:filter-active={searchQuery?.trim() !== ""}
        />
      </div>
    </div>

    <div class="filters-row">
      <div class="filter-group">
        <select bind:value={selectedPlanId} class="ds-select filter-select" class:filter-active={selectedPlanId !== ''}>
          {#each FILTER_PLAN_OPTIONS as plan}
            <option value={plan.value}>{plan.label}</option>
          {/each}
        </select>
        <select bind:value={selectedTrialStatus} class="ds-select filter-select" class:filter-active={selectedTrialStatus !== ''}>
          {#each trialStatusOptions as status}
            <option value={status.value}>{status.label}</option>
          {/each}
        </select>
        <select bind:value={selectedDeviceType} class="ds-select filter-select" class:filter-active={selectedDeviceType !== ''}>
          <option value="">All Device Types</option>
          {#each deviceTypes as deviceType}
            <option value={deviceType}>{deviceType}</option>
          {/each}
        </select>
      </div>
      <div class="filter-group">
        <input
          type="number"
          min="0"
          bind:value={minSongCount}
          placeholder="Songs >"
          class="ds-input filter-input"
          class:filter-active={minSongCount !== ''}
        />
        <input
          type="number"
          min="0"
          bind:value={minMemberCount}
          placeholder="Members >"
          class="ds-input filter-input"
          class:filter-active={minMemberCount !== ''}
        />
        <div class="more-filters-container">
          <button class="more-filters-btn" on:click={toggleMoreFilters} title="More filters" class:filter-active={!!(minFolderCount || minPlaylistCount || minProjectCount)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="2"/>
              <circle cx="12" cy="12" r="2"/>
              <circle cx="12" cy="19" r="2"/>
            </svg>
            {#if minFolderCount || minPlaylistCount || minProjectCount}
              <span class="filter-badge"></span>
            {/if}
          </button>
          {#if showMoreFilters}
            <div class="more-filters-dropdown">
              <div class="dropdown-header">Additional Filters</div>
              <div class="dropdown-item">
                <label for="minFolderCount">Folders ≥</label>
                <input
                  id="minFolderCount"
                  type="number"
                  min="0"
                  bind:value={minFolderCount}
                  placeholder="0"
                  class="ds-input dropdown-input"
                />
              </div>
              <div class="dropdown-item">
                <label for="minPlaylistCount">Playlists ≥</label>
                <input
                  id="minPlaylistCount"
                  type="number"
                  min="0"
                  bind:value={minPlaylistCount}
                  placeholder="0"
                  class="ds-input dropdown-input"
                />
              </div>
              <div class="dropdown-item">
                <label for="minProjectCount">Projects ≥</label>
                <input
                  id="minProjectCount"
                  type="number"
                  min="0"
                  bind:value={minProjectCount}
                  placeholder="0"
                  class="ds-input dropdown-input"
                />
              </div>
            </div>
          {/if}
        </div>
      </div>
    </div>

    {#if loading}
      <div class="ds-loading loading-flex">
        <div class="ds-spinner"></div>
        <div>Loading workspaces...</div>
      </div>
    {:else if error}
      <div class="ds-error error-flex">
        <div>Failed to load workspaces: {error}</div>
        <button class="ds-btn ds-btn-secondary" on:click={fetchWorkspaces}>
          Try Again
        </button>
      </div>
    {:else if workspaces.length === 0}
      <div class="ds-empty">No workspaces found</div>
    {:else}
      <div class="ds-card table-card">
        <table class="ds-table ds-table-clickable">
          <thead>
            <tr>
              <th class="sortable" on:click={() => sortWorkspaces("title")}>Title
                {#if sortField === "title"}
                  <span class="sort-indicator">{sortDirection === "asc" ? "↑" : "↓"}</span>
                {/if}
              </th>
              <th class="sortable" on:click={() => sortWorkspaces("string_id")}>ID
                {#if sortField === "string_id"}
                  <span class="sort-indicator">{sortDirection === "asc" ? "↑" : "↓"}</span>
                {/if}
              </th>
              <th class="sortable" on:click={() => sortWorkspaces("audio_count")}>Audio
                {#if sortField === "audio_count"}
                  <span class="sort-indicator">{sortDirection === "asc" ? "↑" : "↓"}</span>
                {/if}
              </th>
              <th class="sortable" on:click={() => sortWorkspaces("member_count")}>Members
                {#if sortField === "member_count"}
                  <span class="sort-indicator">{sortDirection === "asc" ? "↑" : "↓"}</span>
                {/if}
              </th>
              <th>Device Types</th>
              <th class="sortable" on:click={() => sortWorkspaces("email")}>Owner
                {#if sortField === "email"}
                  <span class="sort-indicator">{sortDirection === "asc" ? "↑" : "↓"}</span>
                {/if}
              </th>
              <th class="sortable" on:click={() => sortWorkspaces("created_at")}>Created
                {#if sortField === "created_at"}
                  <span class="sort-indicator">{sortDirection === "asc" ? "↑" : "↓"}</span>
                {/if}
              </th>
              <th class="sortable" on:click={() => sortWorkspaces("deleted_at")}>Deleted
                {#if sortField === "deleted_at"}
                  <span class="sort-indicator">{sortDirection === "asc" ? "↑" : "↓"}</span>
                {/if}
              </th>
            </tr>
          </thead>
          <tbody>
            {#each filteredWorkspaces as workspace}
              <tr class:deleted={workspace.deleted_at} on:click={() => handleWorkspaceClick(workspace.string_id)}>
                <td>
                  <div class="workspace-title">{workspace.title || "Untitled Workspace"}</div>
                </td>
                <td>
                  <div class="workspace-id" title={workspace.string_id}>{workspace.string_id}</div>
                </td>
                <td>{workspace.audio_count ?? '-'}</td>
                <td>{workspace.member_count ?? '-'}</td>
                <td>
                  {#if workspace.device_types}
                    <span class="device-types">{workspace.device_types.split(',').join(', ')}</span>
                  {:else}
                    <span class="text-muted">-</span>
                  {/if}
                </td>
                <td>
                  <div class="owner-email" title={workspace.email}>{workspace.email}</div>
                </td>
                <td>{formatDate(workspace.created_at)}</td>
                <td>
                  {#if workspace.deleted_at}
                    <span class="ds-chip ds-chip-danger">{formatDate(workspace.deleted_at)}</span>
                  {:else}
                    -
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      {#if totalPages > 1}
        <div class="pagination">
          <div class="pagination-info">
            <label for="page-size" class="pagination-muted">Per page</label>
            <select id="page-size" value={limit} on:change={onLimitChange} class="ds-select page-size-select">
              {#each PAGE_SIZE_OPTIONS as size}
                <option value={size}>{size}</option>
              {/each}
            </select>
          </div>
          <div class="pagination-buttons">
            <button class="ds-btn ds-btn-secondary" on:click={() => goToPage(currentPage - 1)} disabled={!hasPrevPage} title="Previous page">
              Previous
            </button>
            <span class="pagination-label pagination-muted">Page {currentPage} of {totalPages}</span>
            <button class="ds-btn ds-btn-secondary" on:click={() => goToPage(currentPage + 1)} disabled={!hasNextPage} title="Next page">
              Next
            </button>
          </div>
        </div>
      {/if}
    {/if}
  </div>
{/if}

<style>
  .container {
    padding: 2rem;
    max-width: 1400px;
    margin: 0 auto;
  }

  .search-wrap {
    width: 280px;
    max-width: 100%;
  }

  .filters-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;
  }

  .filter-group {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }

  .filter-select {
    min-width: 160px;
    width: auto;
  }

  .filter-input {
    width: 110px;
  }

  .filter-select.filter-active,
  .filter-input.filter-active,
  :global(.ds-search.filter-active),
  .more-filters-btn.filter-active {
    border-color: var(--ds-accent);
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  }

  .more-filters-container {
    position: relative;
  }

  .more-filters-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 1px solid var(--ds-border-3);
    border-radius: var(--ds-radius-sm);
    background: var(--ds-surface);
    color: var(--ds-text-muted);
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
  }

  .more-filters-btn:hover {
    background: var(--ds-surface-3);
    border-color: var(--ds-border-3);
  }

  .filter-badge {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 8px;
    height: 8px;
    background: var(--ds-accent);
    border-radius: 50%;
  }

  .more-filters-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background: var(--ds-surface);
    border: 1px solid var(--ds-border);
    border-radius: var(--ds-radius-md);
    box-shadow: var(--ds-shadow-pop);
    min-width: 220px;
    z-index: 100;
    overflow: hidden;
  }

  .dropdown-header {
    padding: 0.75rem 1rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--ds-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: var(--ds-surface-3);
    border-bottom: 1px solid var(--ds-border-3);
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--ds-border-2);
  }

  .dropdown-item:last-child {
    border-bottom: none;
  }

  .dropdown-item label {
    font-size: 0.875rem;
    color: var(--ds-text);
    font-weight: 500;
  }

  .dropdown-input {
    width: 80px;
    text-align: right;
  }

  .loading-flex {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .error-flex {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
  }

  .table-card {
    padding: 0;
    overflow: hidden;
  }

  .sortable {
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
  }

  .sortable:hover {
    background: var(--ds-surface-2);
  }

  .sort-indicator {
    margin-left: 0.5rem;
    color: var(--ds-accent);
  }

  .workspace-title {
    font-weight: 500;
    color: var(--ds-text);
    font-size: 1rem;
  }

  .workspace-id {
    font-family: var(--ds-mono);
    color: var(--ds-text-muted);
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .owner-email {
    color: var(--ds-text);
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  tr.deleted {
    opacity: 0.7;
  }

  :global(.ds-table-clickable) tr.deleted:hover td {
    background: var(--ds-danger-soft);
  }

  .device-types {
    font-size: 0.75rem;
    color: var(--ds-text-muted);
    font-family: var(--ds-mono);
  }

  .text-muted {
    color: var(--ds-text-muted);
  }

  .pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    margin: 1.5rem 0 2rem;
    padding: 0 0.25rem;
  }

  .pagination-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .pagination-muted {
    font-size: 0.875rem;
    color: var(--ds-text-muted);
  }

  .page-size-select {
    width: auto;
  }

  .pagination-buttons {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  @media (max-width: 768px) {
    .container {
      padding: 1rem;
    }

    .search-wrap {
      width: 100%;
    }

    .filters-row {
      flex-direction: column;
      align-items: stretch;
    }

    .filter-group {
      flex-wrap: wrap;
    }

    .filter-select {
      flex: 1;
      min-width: 140px;
    }

    .table-card {
      overflow-x: auto;
    }

    :global(.table-card table) {
      min-width: 800px;
    }
  }

  .detail-container {
    padding: 2rem;
  }

  .back-button {
    margin-bottom: 1.5rem;
  }

  .back-button svg {
    width: 20px;
    height: 20px;
  }
</style>
